import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CabHireEnterpriseSearchInterface,
  UserServices,
  ServiceProvider
} from '@sabstravtech/obtservices/base';
import {
  CabHireLocationInput,
  CabHireQuote,
  EnterpriseBasketService,
  EnterpriseSearchService,
  UserService,
  CabHireResult,
  AddressType,
  ServiceType,
  ChatbotTaxiSearchService
} from '@sabstravtech/obtservices/angular';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';

import { Geolocation } from '../../../core/classes/geolocation';
import { resultIcons } from '../../../core/enum/result-icons.enum';
import { SortMethod, SortType } from '../../interfaces/sort-type.interface';
import { ThemeService } from '../../../core/services/theme.service';
import { BaseResultsComponent } from '../../../core/components/base_components/base-results.component';

@Component({
  selector: 'app-taxis',
  templateUrl: './taxis.component.html',
  styleUrls: ['./taxis.component.scss'],
  standalone: false
})
export class TaxisComponent extends BaseResultsComponent implements OnInit, OnDestroy {
  cabIconUrl = 'https://images.sabscorp.com/images/TourOp/png/cmac_small.png';
  // readonly cabIconUrl = 'https://images.sabscorp.com/images/TourOp/svg/CAB.svg';

  mapView = false;
  loadingMsg: string;
  selectedItem: CabHireResult;
  pins: (Geolocation & { message: string })[];
  geolocation: Geolocation;
  itemSelected: EventEmitter<CabHireResult> = new EventEmitter();
  resultItemType = resultIcons;
  public taxiServiceType = ServiceType.Cab;
  private lastFocusedTaxiIndex: number | null = null;

  sortingTypes: SortType[] = [
    {
      title: 'Price',
      method: SortMethod.sortByPrice,
      icon: 'ico-dollar',
      dir: true
    }
  ];
  searchParams: CabHireEnterpriseSearchInterface;
  displayItinerary = false;

  constructor(
    public searchService: EnterpriseSearchService,
    private route: ActivatedRoute,
    private basketService: EnterpriseBasketService,
    userService: UserService,
    translateService: TranslateService,
    titleService: Title,
    themeService: ThemeService,
    router: Router,
    public chatbotTaxiSearchService: ChatbotTaxiSearchService
  ) {
    super(titleService, translateService, themeService, searchService, userService, router);
    this.searchParams = this.searchService.searches[ServiceType.Cab];
  }

  get vias(): CabHireLocationInput[] {
    return this.searchParams?.vias;
  }

  get filteredResults$(): Observable<CabHireQuote[]> {
    return this.searchParams.results.pipe(
      map((results: CabHireQuote[]) => {
        const filterId = this.chatbotTaxiSearchService.chatbotFilterId;
        if (!filterId) return results;
        return results.filter((r: CabHireQuote) => r.id === filterId);
      })
    );
  }

  ngOnInit() {
    this.setResultsTitle('Taxi results');
    this.initBaseResults();

    this.subscribe(this.route.queryParams, ({ center }) => {
      this.mapView = !!center;
    });

    this.subscribe(this.searchParams.isLoading, (isLoading: boolean) => {
      if (!isLoading) {
        this.searchParams.sortBy(false);
      }
    });
    this.displayItinerary = this.baseUserService.isBasketReplacementUserFavouriteSet();
  }

  ngOnDestroy(): void {
    this.chatbotTaxiSearchService.chatbotFilterId = null;
    super.ngOnDestroy();
  }

  addToCart(item: CabHireQuote): void {
    this.searchService.searches[ServiceType.Cab].addCabHireToBasket(
      item,
      this.searchService.searches[ServiceType.Cab].getSearchQuery()
    );
    this.basketService.toggleMenu();
  }

  goToMapView(taxi: any, itemIndex: number): void {
    this.lastFocusedTaxiIndex = itemIndex;

    this.geolocation = new Geolocation(
      `${taxi.fromLocation.latitude},${taxi.fromLocation.longitude}`
    );

    this.pins = taxi.vias.map((via) => ({
      ...new Geolocation(`${via?.latitude},${via?.longitude}`),
      message: ''
    }));

    this.pins.push({
      ...new Geolocation(`${taxi.toLocation.latitude},${taxi.toLocation.longitude}`),
      message: ''
    });
    this.pins = this.pins.filter((pin) => !Number.isNaN(pin.lat) && !Number.isNaN(pin.lng));
    this.selectedItem = taxi;
    this.cabIconUrl = this.getLogo(taxi);
    this.itemSelected.emit(taxi);
    this.mapView = true;
  }

  onCloseMapView(): void {
    this.mapView = false;
    if (this.lastFocusedTaxiIndex !== null) {
      setTimeout(() => {
        const button = document.getElementById(`taxi-map-btn-${this.lastFocusedTaxiIndex}`);
        if (button) {
          button.focus();
        }
        this.lastFocusedTaxiIndex = null;
      }, 100);
    }
  }

  skipToResultContent(): boolean {
    return super.skipToResultContent('taxi');
  }

  getLogo(item: CabHireQuote): string {
    switch (item.source) {
      case ServiceProvider.CMAC:
        return 'https://images.sabscorp.com/images/TourOp/png/cmac_small.png';
      case ServiceProvider.Groundspan:
        return 'https://images.sabscorp.com/images/TourOp/groundspan-small.jpg';
      case ServiceProvider.Groundscope:
        return 'https://images.sabscorp.com/images/TourOp/GRS.gif';
    }

    return '';
  }
}
